/**
 * StackedPins Component
 *
 * Renders 1-3 comment pins with diagonal offset for multiple threads on the same element.
 * Each pin is independently clickable and opens that specific thread.
 *
 * Visual layout (Figma model):
 * - Pin 1: at element corner (top-right)
 * - Pin 2: offset DOWN and LEFT from Pin 1
 * - Pin 3: offset DOWN and LEFT from Pin 2
 * - Later pins have higher z-index (appear in front)
 *
 * Features:
 * - Hover preview tooltip showing author, text preview, and reply count
 * - Active pin comes to front (z-index 50)
 * - Smooth transitions for z-index and hover states
 */

import React, { useState, useRef } from 'react';
import { useFloating, offset, flip, shift, arrow, autoUpdate } from '@floating-ui/react';
import { CommentPin } from './CommentPin';
import type { Comment, TeamMember } from '../../lib/types';
import { tokens } from '../../styles/tokens';

/** Offset in pixels between stacked pins (pins stack DOWN and LEFT) */
const STACK_OFFSET_PX = 14;

/** Base z-index for stacked pins (increments for each subsequent pin so newer pins are on top) */
const BASE_Z_INDEX = 30;

/** Z-index for active/hovered pin to bring it to front */
const ACTIVE_Z_INDEX = 50;

export interface StackedPinsProps {
  /** Threads to display as stacked pins (1-3 threads) */
  threads: Comment[];
  /** Base position for the stack (top-right of element) */
  basePosition: { top: number; left: number };
  /** Set of unread comment IDs */
  unreadCommentIds?: Set<string>;
  /** Callback when a pin is clicked */
  onPinClick: (comment: Comment, pinElement: HTMLElement) => void;
  /** Callback when hovering starts on a pin */
  onPinHover?: (commentId: string | null) => void;
  /** Currently expanded comment ID */
  expandedCommentId?: string;
  /** Map of comment ID to reply count */
  replyCounts: Map<string, number>;
  /** Map of comment ID to pin number */
  pinNumbers: Map<string, number>;
  /** Map of author ID to team member info */
  authors?: Map<string, TeamMember>;
  /** Whether sidebar is open (for collision detection) */
  isSidebarOpen?: boolean;
}

/**
 * Get initials from a name
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Truncate text to a maximum length
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

export const StackedPins: React.FC<StackedPinsProps> = ({
  threads,
  basePosition,
  unreadCommentIds = new Set(),
  onPinClick,
  onPinHover,
  expandedCommentId,
  replyCounts,
  pinNumbers,
  authors = new Map(),
  isSidebarOpen = false,
}) => {
  const [hoveredThreadId, setHoveredThreadId] = useState<string | null>(null);
  const pinRefs = useRef<Map<string, HTMLElement>>(new Map());
  const arrowRef = useRef<HTMLDivElement>(null);

  // Parse sidebar width from token (e.g., "360px" -> 360)
  const sidebarWidth = parseInt(tokens.layout.sidebarWidth, 10);
  const viewportPadding = 16;

  // Setup floating-ui for preview with arrow
  const { refs, floatingStyles, isPositioned, placement, middlewareData } = useFloating({
    placement: 'top',
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: ['bottom', 'top', 'left', 'right'],
        padding: isSidebarOpen
          ? { top: viewportPadding, bottom: viewportPadding, left: viewportPadding, right: sidebarWidth + viewportPadding }
          : viewportPadding,
      }),
      shift({
        padding: isSidebarOpen
          ? { top: viewportPadding, bottom: viewportPadding, left: viewportPadding, right: sidebarWidth + viewportPadding }
          : viewportPadding,
      }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });

  const handleHoverStart = (commentId: string, pinElement: HTMLElement) => {
    setHoveredThreadId(commentId);
    pinRefs.current.set(commentId, pinElement);
    refs.setReference(pinElement);
    onPinHover?.(commentId);
  };

  const handleHoverEnd = () => {
    setHoveredThreadId(null);
    onPinHover?.(null);
  };

  return (
    <>
      {threads.map((thread, index) => {
        const pinNumber = pinNumbers.get(thread.id) ?? index + 1;
        const replyCount = replyCounts.get(thread.id) ?? 0;
        const authorMember = authors.get(thread.authorId);
        const author = authorMember
          ? {
              name: authorMember.name,
              initials: getInitials(authorMember.name),
              avatar: authorMember.avatar,
            }
          : undefined;

        // Calculate offset position for this pin
        // First pin (index 0) is at base position (element corner)
        // Subsequent pins stack DOWN (positive top) and LEFT (negative left)
        const position = {
          top: basePosition.top + index * STACK_OFFSET_PX,
          left: basePosition.left - index * STACK_OFFSET_PX,
        };

        const isActive = expandedCommentId === thread.id;

        // Z-index: Active pin comes to front, otherwise later pins appear on top
        const zIndex = isActive ? ACTIVE_Z_INDEX : BASE_Z_INDEX - index;

        return (
          <div
            key={thread.id}
            style={{
              position: 'absolute',
              top: `${position.top}px`,
              left: `${position.left}px`,
              pointerEvents: 'auto',
              zIndex,
              transition: `top 50ms linear, left 50ms linear, z-index 0s, transform ${tokens.animation.duration} ${tokens.animation.timing}`,
            }}
            onMouseEnter={(e) => handleHoverStart(thread.id, e.currentTarget)}
            onMouseLeave={handleHoverEnd}
          >
            <CommentPin
              comment={thread}
              pinNumber={pinNumber}
              onClick={onPinClick}
              isExpanded={isActive}
              isUnread={unreadCommentIds.has(thread.id)}
              replyCount={replyCount > 0 ? replyCount : undefined}
              author={author}
            />
          </div>
        );
      })}

      {/* Floating preview - shown when hovering over a pin */}
      {hoveredThreadId && !expandedCommentId && (() => {
        const hoveredThread = threads.find(t => t.id === hoveredThreadId);
        if (!hoveredThread) return null;

        const replyCount = replyCounts.get(hoveredThread.id) ?? 0;
        const authorMember = authors.get(hoveredThread.authorId);
        const author = authorMember
          ? {
              name: authorMember.name,
              initials: getInitials(authorMember.name),
              avatar: authorMember.avatar,
            }
          : undefined;

        // Get arrow position from middleware
        const arrowX = middlewareData.arrow?.x;
        const arrowY = middlewareData.arrow?.y;
        const side = placement.split('-')[0] as 'top' | 'bottom' | 'left' | 'right';

        return (
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              width: '192px',
              backgroundColor: tokens.colors.gray800,
              color: tokens.colors.textReverse,
              fontSize: '12px',
              borderRadius: '6px',
              boxShadow: tokens.elevation.overlay,
              padding: `${tokens.spacing['2xs']} ${tokens.spacing.xs}`,
              zIndex: ACTIVE_Z_INDEX + 10,
              pointerEvents: 'none',
              visibility: isPositioned ? 'visible' : 'hidden',
              opacity: isPositioned ? 1 : 0,
              transition: 'opacity 150ms ease-out',
              willChange: 'transform, opacity',
            }}
          >
            {/* Author name */}
            <div
              style={{
                fontWeight: tokens.typography.fontWeight.semibold,
                marginBottom: tokens.spacing['5xs'],
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {author?.name || 'Unknown'}
            </div>

            {/* Comment text preview */}
            <div
              style={{
                color: tokens.colors.gray300,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {truncateText(hoveredThread.content, 50)}
            </div>

            {/* Reply count */}
            {replyCount > 0 && (
              <div
                style={{
                  color: tokens.colors.gray400,
                  fontSize: '10px',
                  marginTop: tokens.spacing['5xs'],
                }}
              >
                {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
              </div>
            )}

            {/* Dynamic arrow positioned by floating-ui */}
            <div
              ref={arrowRef}
              style={{
                position: 'absolute',
                width: 0,
                height: 0,
                ...(side === 'top' && {
                  bottom: '-5px',
                  left: arrowX != null ? `${arrowX}px` : '50%',
                  transform: arrowX != null ? undefined : 'translateX(-50%)',
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: `6px solid ${tokens.colors.gray800}`,
                }),
                ...(side === 'bottom' && {
                  top: '-5px',
                  left: arrowX != null ? `${arrowX}px` : '50%',
                  transform: arrowX != null ? undefined : 'translateX(-50%)',
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderBottom: `6px solid ${tokens.colors.gray800}`,
                }),
                ...(side === 'left' && {
                  right: '-5px',
                  top: arrowY != null ? `${arrowY}px` : '50%',
                  transform: arrowY != null ? undefined : 'translateY(-50%)',
                  borderTop: '6px solid transparent',
                  borderBottom: '6px solid transparent',
                  borderLeft: `6px solid ${tokens.colors.gray800}`,
                }),
                ...(side === 'right' && {
                  left: '-5px',
                  top: arrowY != null ? `${arrowY}px` : '50%',
                  transform: arrowY != null ? undefined : 'translateY(-50%)',
                  borderTop: '6px solid transparent',
                  borderBottom: '6px solid transparent',
                  borderRight: `6px solid ${tokens.colors.gray800}`,
                }),
              }}
            />
          </div>
        );
      })()}
    </>
  );
};
